// Client-side validation for video evidence. MIME + size are sync; duration
// requires loading the file's metadata into a temp <video>, so the check is
// async. The server (api.vesr) enforces the same MIME/size/1-video limits —
// this exists for fast, friendly feedback before a 500MB upload starts. Duration
// is checked here only — the server has no duration gate.
import { ALLOWED_VIDEO_TYPES, MAX_VIDEO_BYTES, MAX_VIDEO_DURATION_SEC } from '../../../interface/vesr';
import { formatBytes } from '../helper';

export interface IVideoValidationResult {
	ok: boolean;
	error?: string;
	durationSec?: number;
}

export const validateVideoFile = (file: File): Promise<IVideoValidationResult> => {
	if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
		return Promise.resolve({ ok: false, error: 'Use an MP4, WebM or MOV video for this report.' });
	}
	if (file.size > MAX_VIDEO_BYTES) {
		// Derived from the constant, never hardcoded — the two drifted apart before.
		return Promise.resolve({ ok: false, error: `Video must be ${formatBytes(MAX_VIDEO_BYTES)} or less — try a shorter clip.` });
	}
	return new Promise((resolve) => {
		const url = URL.createObjectURL(file);
		const probe = document.createElement('video');
		probe.preload = 'metadata';
		probe.onloadedmetadata = () => {
			const duration = probe.duration;
			URL.revokeObjectURL(url);
			// Freshly recorded blobs can report Infinity/NaN until seeked (Safari/
			// Chrome quirk) — accept those; the recorder's own auto-stop at the cap
			// and the server's size cap are the backstops.
			if (!Number.isFinite(duration)) {
				resolve({ ok: true });
				return;
			}
			if (duration > MAX_VIDEO_DURATION_SEC + 0.5) {
				resolve({ ok: false, error: `Video must be ${describeDuration(MAX_VIDEO_DURATION_SEC)} or less.` });
				return;
			}
			resolve({ ok: true, durationSec: duration });
		};
		probe.onerror = () => {
			URL.revokeObjectURL(url);
			resolve({ ok: false, error: 'Could not read this video — try a different file.' });
		};
		probe.src = url;
	});
};

/**
 * A cap in words for refusal copy: "3 minutes", "1 minute", "90 seconds".
 * Android and iOS assert the same table, so the three apps say the same thing.
 */
export const describeDuration = (seconds: number) => {
	const s = Math.max(0, Math.round(seconds));
	if (s > 0 && s % 60 === 0) {
		const minutes = s / 60;
		return minutes === 1 ? '1 minute' : `${minutes} minutes`;
	}
	return `${s} seconds`;
};

export const formatDuration = (seconds: number) => {
	const s = Math.max(0, Math.round(seconds));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
